<template>
  <div class="fr-container">
    <PremiumPage
      title="Monthly Financial Report"
      subtitle="Finance / Financial Report"
      :loading="false"
      showBack
      contentClass="pa-4"
    >
      <template #header-actions>
        <button
          type="button"
          class="fr-btn fr-btn-excel"
          @click="exportExcel"
          :disabled="exporting"
        >
          <v-icon size="15" color="white">mdi-file-excel</v-icon>
          <span>{{ exporting ? 'Exporting…' : 'Export Excel' }}</span>
        </button>
        <button
          type="button"
          class="fr-btn fr-btn-pdf"
          @click="exportPdf"
          :disabled="exportingPdf"
        >
          <v-icon size="15" color="white">mdi-file-pdf-box</v-icon>
          <span>{{ exportingPdf ? 'Exporting…' : 'Export PDF' }}</span>
        </button>
      </template>

      <!-- Month Chip Bar -->
      <div class="fr-month-bar">
        <span class="fr-month-label">SELECT MONTH</span>
        <div class="fr-month-chips">
          <div
            v-for="opt in monthOptions"
            :key="opt.value"
            class="fr-month-chip"
            :class="{ active: selectedMonth && selectedMonth.value === opt.value }"
            @click="selectMonth(opt)"
          >
            {{ opt.label }}
          </div>
        </div>
      </div>

      <!-- Chart Panel -->
      <div class="fr-card mb-5">
        <div class="fr-card-header">
          <div>
            <div class="fr-card-title">Bills vs Expenses — Monthly Overview</div>
            <div class="fr-card-sub">Last 6 months · Click a bar to switch month</div>
          </div>
          <div class="fr-legend">
            <div class="fr-legend-item">
              <span class="fr-legend-dot" style="background:#10B981"></span> Collected
            </div>
            <div class="fr-legend-item">
              <span class="fr-legend-dot" style="background:#F59E0B"></span> Pending
            </div>
            <div class="fr-legend-item">
              <span class="fr-legend-dot" style="background:#EF4444"></span> Expenses
            </div>
          </div>
        </div>
        <div class="fr-card-body chart-body">
          <div v-if="chartLoading" class="fr-center-loader">
            <v-progress-circular indeterminate :color="spinnerColor" size="28"></v-progress-circular>
            <span class="ml-2 fr-card-sub">Loading chart…</span>
          </div>
          <bar-chart
            v-else-if="chartData"
            :chart-data="chartData"
            :options="chartOptions"
            style="height:100%;"
          />
          <div v-else class="fr-empty-msg">No chart data available</div>
        </div>
      </div>

      <!-- Statement Table -->
      <div class="fr-card">
        <div class="fr-stmt-header">
          <div>
            <div class="fr-card-title">Statement — {{ selectedMonthLabel }}</div>
            <div class="fr-card-sub">All collections and expenses in one view</div>
          </div>
          <div class="fr-filter-pills">
            <div
              v-for="f in filters"
              :key="f.key"
              class="fr-pill"
              :class="{ active: activeFilter === f.key }"
              @click="activeFilter = f.key"
            >
              {{ f.label }}
              <span class="fr-pill-count">{{ filterCount(f.key) }}</span>
            </div>
          </div>
        </div>

        <div v-if="collectionsLoading || expensesLoading" class="fr-center-loader" style="padding:40px 0;">
          <v-progress-circular indeterminate :color="spinnerColor" size="28"></v-progress-circular>
        </div>

        <div v-else class="fr-table-wrap">
          <table class="fr-table">
            <thead>
              <tr>
                <th style="width:48px">#</th>
                <th style="width:110px">Date</th>
                <th>Description</th>
                <th style="width:145px">Type</th>
                <th class="text-right" style="width:130px">Credit (₹)</th>
                <th class="text-right" style="width:120px">Debit (₹)</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="filteredTransactions.length">
                <tr v-for="(txn, i) in filteredTransactions" :key="i" class="fr-tr">
                  <td class="fr-index-cell">{{ txn.rowNumber }}</td>
                  <td class="fr-date-cell">{{ txn.dateStr }}</td>
                  <td>
                    <div class="fr-txn-name">{{ txn.title }}</div>
                    <div class="fr-txn-sub">{{ txn.subTitle }}</div>
                  </td>
                  <td>
                    <span class="fr-badge" :class="'badge-' + txn.txnType">{{ txn.typeLabel }}</span>
                    <!-- <span v-if="txn.recurring" class="fr-badge badge-recurring ml-1">Recurring</span> -->
                  </td>
                  <td class="text-right">
                    <span v-if="txn.credit > 0" :class="'fr-amount fr-amount-' + txn.txnType">+{{ formatNum(txn.credit) }}</span>
                    <span v-else class="fr-dash">—</span>
                  </td>
                  <td class="text-right">
                    <span v-if="txn.debit > 0" class="fr-amount fr-amount-debit">−{{ formatNum(txn.debit) }}</span>
                    <span v-else class="fr-dash">—</span>
                  </td>
                </tr>
              </template>
              <tr v-else>
                <td colspan="6" class="fr-empty-row">No transactions for the selected period / filter</td>
              </tr>
            </tbody>
            <tfoot v-if="filteredTransactions.length">
              <tr class="fr-tfoot-row">
                <td colspan="4">{{ selectedMonthLabel }} — Total</td>
                <td class="text-right">
                  <template v-if="activeFilter === 'all'">
                    <div class="fr-amount fr-amount-credit">
                      {{ formatCurrency(reportTotals.totalCredits) }} 
                    </div>
                    <div v-if="reportTotals.totalPending > 0" class="orange-val" style="font-size: 11px; font-weight: 500; margin-top: 2px;">
                      + {{ formatCurrency(reportTotals.totalPending) }} pending
                    </div>
                  </template>
                  <template v-else-if="activeFilter === 'credit'">
                    <div class="fr-amount fr-amount-credit">
                      {{ formatCurrency(totalCredit) }}
                    </div>
                  </template>
                  <template v-else-if="activeFilter === 'pending'">
                    <div class="orange-val">
                      {{ formatCurrency(totalCredit) }} pending
                    </div>
                  </template>
                  <template v-else>
                    <span class="fr-dash">—</span>
                  </template>
                </td>
                <td class="text-right">
                  <span v-if="totalDebit > 0" class="fr-amount fr-amount-debit">
                    {{ formatCurrency(totalDebit) }}
                  </span>
                  <span v-else class="fr-dash">—</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

    </PremiumPage>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import BarChart from '../../societyadmin/BarChart.js'
import PremiumPage from '../../common/PremiumPage.vue'
import axios from '@/helpers/axios'
import moment from 'moment'

export default {
  name: 'FinancialReportDetails',
  components: { BarChart, PremiumPage },

  computed: {
    ...mapState('theme', ['buttonColor', 'isDark']),

    selectedMonthLabel () {
      return this.selectedMonth ? this.selectedMonth.label : ''
    },

    /** All transactions returned from the backend */
    allTransactions () {
      return this.transactions.map(t => {
        let icon = '💰'
        if (t.txnType === 'debit') icon = '💸'
        else if (t.txnType === 'pending') icon = '⏳'
        return {
          ...t,
          icon
        }
      })
    },

    filteredTransactions () {
      if (this.activeFilter === 'all') return this.allTransactions
      return this.allTransactions.filter(t => t.txnType === this.activeFilter)
    },

    totalCredit () {
      return this.filteredTransactions.reduce((s, t) => s + t.credit, 0)
    },
    totalDebit () {
      return this.filteredTransactions.reduce((s, t) => s + t.debit, 0)
    },
    spinnerColor () {
      return this.isDark ? 'white' : 'primary'
    }
  },

  data () {
    return {
      // chart
      chartData:    null,
      chartOptions: {},
      chartLoading: false,

      // statement report data
      transactions:       [],
      collectionsLoading: false,
      reportTotals:       { totalCredits: 0, totalDebits: 0, totalPending: 0 },

      // month picker
      monthOptions:  [],
      selectedMonth: null,

      // statement filter
      activeFilter: 'all',
      filters: [
        { key: 'all',     label: 'All'         },
        { key: 'credit',  label: 'Collections' },
        { key: 'debit',   label: 'Expenses'    },
        { key: 'pending', label: 'Pending'     },
      ],

      // export
      exporting:    false,
      exportingPdf: false,
    }
  },

  created () {
    this.buildMonthOptions()
    this.selectedMonth = {
      value: `${moment().month() + 1}-${moment().year()}`,
      label: moment().format('MMMM YYYY'),
    }
    this.loadOverview()
    this.fetchReport()
  },

  methods: {
    buildMonthOptions () {
      const opts = []
      for (let i = 0; i < 6; i++) {
        const m = moment().subtract(i, 'months')
        opts.push({ value: `${m.month() + 1}-${m.year()}`, label: m.format('MMM YYYY') })
      }
      this.monthOptions = opts
    },

    selectMonth (opt) {
      this.selectedMonth = opt
      this.activeFilter  = 'all'
      this.fetchReport()
    },

    onMonthChange () {
      this.activeFilter = 'all'
      this.fetchReport()
    },

    parseSelected () {
      const val = this.selectedMonth && this.selectedMonth.value
        ? String(this.selectedMonth.value)
        : `${moment().month() + 1}-${moment().year()}`
      const [m, y] = val.split('-')
      return { month: Number(m), year: Number(y) }
    },

    loadOverview () {
      this.chartLoading = true
      axios.get('societyadmin/dashboard/finance-overview')
        .then(res => {
          const fd = res.data
          if (fd && fd.months) {
            const tooltipBackground = this.isDark ? '#111827' : '#ffffff'
            const tooltipTitleColor = this.isDark ? '#F8FAFC' : '#0F172A'
            const tooltipBodyColor = this.isDark ? '#D1D5DB' : '#334155'
            const tooltipBorderColor = this.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
            const axisTickColor = this.isDark ? '#D1D5DB' : '#9CA3AF'
            const gridColor = this.isDark ? 'rgba(241,245,249,0.08)' : '#f0f0f0'

            this.chartData = {
              labels: fd.months,
              datasets: [
                { type: 'bar', label: 'Collected', stack: 'bills',    data: fd.collected, backgroundColor: '#10B981', borderRadius: 4, barPercentage: 0.6 },
                { type: 'bar', label: 'Pending',   stack: 'bills',    data: fd.pending,   backgroundColor: '#F59E0B', borderRadius: 4, barPercentage: 0.6 },
                { type: 'bar', label: 'Expenses',  stack: 'expenses', data: fd.expenses,  backgroundColor: '#EF4444', borderRadius: 4, barPercentage: 0.6 },
              ],
            }
            this.chartOptions = {
              responsive: true,
              maintainAspectRatio: false,
              hover: { mode: 'index', intersect: false },
              tooltips: {
                mode: 'index',
                intersect: false,
                backgroundColor: tooltipBackground,
                cornerRadius: 10,
                xPadding: 12,
                yPadding: 12,
                caretPadding: 10,
                titleFontSize: 13,
                titleFontColor: tooltipTitleColor,
                titleMarginBottom: 10,
                bodyFontSize: 12,
                bodyFontColor: tooltipBodyColor,
                bodySpacing: 8,
                borderColor: tooltipBorderColor,
                borderWidth: 1,
                displayColors: true,
                callbacks: {
                  title: items => {
                    return items && items[0] && items[0].label ? `${items[0].label} Overview` : ''
                  },
                  labelColor: (tooltipItem, chart) => {
                    const dataset = chart.config.data.datasets[tooltipItem.datasetIndex]
                    return {
                      borderColor: dataset.backgroundColor,
                      backgroundColor: dataset.backgroundColor,
                    }
                  },
                  label: i => {
                    const label = i.datasetLabel || ''
                    const val = Number(i.yLabel || 0)
                    return ` ${label}: ₹${val.toLocaleString()}`
                  },
                },
              },
              scales: {
                xAxes: [{ gridLines: { display: false }, ticks: { fontColor: axisTickColor, fontSize: 11 } }],
                yAxes: [{
                  gridLines: { color: gridColor },
                  ticks: {
                    fontColor: axisTickColor,
                    fontSize: 11,
                    callback: v => '₹' + (v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v),
                  },
                }],
              },
              onClick: (e, elements) => {
                if (!elements.length) return
                const el = elements[0]
                const idx = el._index !== undefined ? el._index : el.index
                const monthsAgo = 5 - idx
                const m = moment().subtract(monthsAgo, 'months')
                this.selectMonth({ value: `${m.month() + 1}-${m.year()}`, label: m.format('MMMM YYYY') })
              },
            }
          } else {
            this.chartData = null
          }
          this.chartLoading = false
        })
        .catch(() => { this.chartLoading = false; this.chartData = null })
    },

    fetchReport () {
      const { month, year } = this.parseSelected()
      this.collectionsLoading = true
      axios.get('societyadmin/dashboard/finance-report', { params: { month, year } })
        .then(res => {
          this.transactions       = (res.data && res.data.transactions) ? res.data.transactions : []
          this.reportTotals       = (res.data && res.data.totals) ? res.data.totals : { totalCredits: 0, totalDebits: 0, totalPending: 0 }
          this.collectionsLoading = false
        })
        .catch(() => {
          this.transactions       = []
          this.reportTotals       = { totalCredits: 0, totalDebits: 0, totalPending: 0 }
          this.collectionsLoading = false
        })
    },

    filterCount (key) {
      if (key === 'all') return this.allTransactions.length
      return this.allTransactions.filter(t => t.txnType === key).length
    },

    formatNum (v) {
      return Number(v).toLocaleString('en-IN')
    },
    formatCurrency (v) {
      if (!v) return '₹0'
      return '₹' + Number(v).toLocaleString('en-IN')
    },
    formatReportDate (value) {
      if (value === null || value === undefined || value === '') return '—'
      const numeric = Number(value)
      let date = null
      if (!Number.isNaN(numeric) && numeric > 0) {
        date = moment(numeric)
      } else {
        date = moment(value, moment.ISO_8601, true)
        if (!date.isValid()) {
          date = moment(value)
        }
      }
      return date && date.isValid() ? date.format('DD MMM YYYY') : '—'
    },

    exportExcel () {
      const { month, year } = this.parseSelected()
      this.exporting = true
      axios.get('societyadmin/export/finance-report/excel', { params: { month, year }, responseType: 'blob' })
        .then(res => {
          const url  = window.URL.createObjectURL(new Blob([res.data]))
          const link = document.createElement('a')
          link.href  = url
          link.setAttribute('download', `Monthly_Finance_Report_${month}_${year}.xlsx`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          this.exporting = false
        })
        .catch(() => { this.exporting = false })
    },

    exportPdf () {
      const { month, year } = this.parseSelected()
      this.exportingPdf = true
      axios.get('societyadmin/export/finance-report/pdf', { params: { month, year }, responseType: 'blob' })
        .then(res => {
          const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
          const link = document.createElement('a')
          link.href  = url
          link.setAttribute('download', `Monthly_Finance_Report_${month}_${year}.pdf`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          this.exportingPdf = false
        })
        .catch(() => { this.exportingPdf = false })
    },
  },
}
</script>

<style scoped>
/* ── Container ────────────────────────────────────────────────────── */
.fr-container {
  padding: 24px;
}

/* ── Dark Mode Overrides ──────────────────────────────────────────── */
::v-deep .pp-dark .fr-month-bar,
::v-deep .pp-dark .fr-summary-card,
::v-deep .pp-dark .fr-card,
::v-deep .pp-dark .fr-table {
  background: var(--surface2, #273548) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}
::v-deep .pp-dark .fr-table thead tr {
  background: rgba(255, 255, 255, 0.03) !important;
}
::v-deep .pp-dark .fr-month-chip {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}
::v-deep .pp-dark .fr-pill {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}
::v-deep .pp-dark .fr-tfoot-row td {
  background: rgba(255, 255, 255, 0.03) !important;
  border-top-color: rgba(255, 255, 255, 0.08);
}
::v-deep .pp-dark .fr-table tbody td {
  border-bottom-color: rgba(255, 255, 255, 0.04);
}
::v-deep .pp-dark .fr-table th {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
::v-deep .pp-dark .fr-month-bar,
::v-deep .pp-dark .fr-summary-card,
::v-deep .pp-dark .fr-card {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* ── Page Header ──────────────────────────────────────────────────── */
.fr-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.fr-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--tx, #1e293b);
  margin: 0;
  line-height: 1.3;
}
.fr-breadcrumb {
  font-size: 12px;
  color: var(--tx2, #94a3b8);
  margin-top: 4px;
}
.fr-breadcrumb-active {
  color: var(--primary, #E8706A);
  font-weight: 600;
}
.fr-page-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* ── Buttons ──────────────────────────────────────────────────────── */
.fr-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: opacity 0.18s, transform 0.14s, background-color 0.18s, border-color 0.18s, color 0.18s;
  white-space: nowrap;
}
.fr-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); }
.fr-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.fr-btn-excel,
.fr-btn-pdf {
  background: var(--btn-color, #10B981);
  color: #ffffff;
  border-color: var(--btn-color, #10B981);
}
.fr-btn-pdf:hover,
.fr-btn-excel:hover {
  background: var(--btn-color, #10B981);
  opacity: 0.92;
}

/* ── Month Chip Bar ───────────────────────────────────────────────── */
.fr-month-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--surface, #fff);
  border-radius: 12px;
  padding: 12px 18px;
  box-shadow: 0 1px 6px rgba(0,0,0,.06);
  margin-bottom: 20px;
  overflow-x: auto;
}
.fr-month-label {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--tx2, #94a3b8);
  letter-spacing: .7px;
  white-space: nowrap;
  flex-shrink: 0;
}
.fr-month-chips {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  overflow-x: auto;
  min-width: 0;
}
.fr-month-chip {
  padding: 6px 15px;
  border-radius: 20px;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  border: 1.5px solid var(--border, #e5e7eb);
  background: var(--surface2, #f8fafc);
  color: var(--tx2, #64748b);
  transition: all 0.16s;
  user-select: none;
}
.fr-month-chip:hover {
  border-color: var(--btn-color, #10B981);
  color: var(--btn-color, #10B981);
  background: var(--btn-color-lt, rgba(16,184,129,0.12));
}
.fr-month-chip.active {
  background: var(--btn-color, #10B981);
  color: #fff;
  border-color: var(--btn-color, #10B981);
  font-weight: 600;
}

/* ── Summary Cards ────────────────────────────────────────────────── */
.fr-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}
.fr-summary-card {
  background: var(--surface, #fff);
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: 0 1px 6px rgba(0,0,0,.06);
  display: flex;
  align-items: center;
  gap: 16px;
  border-top: 3.5px solid transparent;
  transition: box-shadow 0.18s;
}
.fr-summary-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,.1); }
.fr-summary-card.green  { border-top-color: #10B981; }
.fr-summary-card.orange { border-top-color: #F59E0B; }
.fr-summary-card.red    { border-top-color: #EF4444; }

.fr-summary-icon-wrap {
  width: 46px; height: 46px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}
.fr-summary-icon-wrap.green  { background: #d1fae5; }
.fr-summary-icon-wrap.orange { background: #fef3c7; }
.fr-summary-icon-wrap.red    { background: #fee2e2; }

.fr-summary-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--tx2, #94a3b8);
  text-transform: uppercase;
  letter-spacing: .5px;
}
.fr-summary-value {
  font-size: 22px;
  font-weight: 700;
  margin-top: 3px;
  line-height: 1.2;
}
.fr-summary-sub {
  font-size: 11px;
  color: var(--tx2, #94a3b8);
  margin-top: 3px;
}
.green-val  { color: #059669; }
.orange-val { color: #D97706; }
.red-val    { color: #DC2626; }

/* ── Card ─────────────────────────────────────────────────────────── */
.fr-card {
  background: var(--surface, #fff);
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0,0,0,.06);
  margin-bottom: 20px;
  overflow: hidden;
}
.mb-5 { margin-bottom: 20px; }

.fr-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 0;
  flex-wrap: wrap;
}
.fr-card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--tx, #1e293b);
}
.fr-card-sub {
  font-size: 11.5px;
  color: var(--tx2, #94a3b8);
  margin-top: 2px;
}
.fr-card-body {
  padding: 16px 20px 20px;
}
.chart-body { height: 260px; }

.fr-legend {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
}
.fr-legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  color: var(--tx2, #64748b);
}
.fr-legend-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  display: inline-block;
}

/* ── Statement Header ─────────────────────────────────────────────── */
.fr-stmt-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--border, #f1f5f9);
  flex-wrap: wrap;
}

/* ── Filter Pills ─────────────────────────────────────────────────── */
.fr-filter-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.fr-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 13px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1.5px solid var(--border, #e5e7eb);
  background: var(--surface2, #f8fafc);
  color: var(--tx2, #64748b);
  transition: all 0.16s;
  user-select: none;
}
.fr-pill:hover {
  border-color: var(--btn-color, #10B981);
  color: var(--btn-color, #10B981);
}
.fr-pill.active {
  background: var(--btn-color, #10B981);
  color: #fff;
  border-color: var(--btn-color, #10B981);
}
.fr-pill-count {
  background: rgba(255,255,255,0.3);
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  min-width: 18px;
  text-align: center;
}
.fr-pill.active .fr-pill-count {
  background: rgba(255,255,255,0.28);
}
.fr-pill:not(.active) .fr-pill-count {
  background: var(--border, #e5e7eb);
  color: var(--tx2, #64748b);
}

/* ── Table ────────────────────────────────────────────────────────── */
.fr-table-wrap {
  width: 100%;
  overflow-x: auto;
}
.fr-table {
  width: 100%;
  border-collapse: collapse;
}
.fr-table thead tr {
  background: var(--surface2, #f8fafc);
}
.fr-table th {
  text-align: left;
  padding: 11px 14px;
}
.fr-txn-name {
  font-weight: 600;
  color: var(--tx, #1e293b);
}
.fr-txn-sub {
  font-size: 12px;
  color: var(--tx2, #64748b);
  margin-top: 3px;
}
.fr-amount-credit {
  color: #10B981;
}
.fr-amount-debit {
  color: #dc2626;
}

.fr-table th {
  text-align: left;
  padding: 11px 14px;
  font-size: 11px;
  font-weight: 700;
  color: var(--tx2, #94a3b8);
  text-transform: uppercase;
  letter-spacing: .5px;
  border-bottom: 2px solid var(--border, #e5e7eb);
  white-space: nowrap;
}
.fr-table th.text-right,
.fr-table td.text-right { text-align: right; }

.fr-table tbody td {
  padding: 12px 14px;
  font-size: 13px;
  color: var(--tx, #374151);
  border-bottom: 1px solid var(--border, #f2f2f2);
  vertical-align: middle;
}
.fr-index-cell {
  text-align: center;
  color: var(--tx2, #64748b);
  font-weight: 600;
}.fr-tr:hover td { background: var(--surface2, #fefcfc); }
.fr-table tbody tr:last-child td { border-bottom: none; }

/* ── Transaction Icon ─────────────────────────────────────────────── */
.fr-txn-icon {
  width: 34px; height: 34px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px;
}
.fr-txn-icon.credit  { background: #d1fae5; }
.fr-txn-icon.debit   { background: #fee2e2; }
.fr-txn-icon.pending { background: #fef3c7; }

.fr-txn-name {
  font-weight: 600;
  color: var(--tx, #1e293b);
  font-size: 13px;
}
.fr-txn-sub {
  font-size: 11px;
  color: var(--tx2, #94a3b8);
  margin-top: 2px;
}

.fr-date-cell {
  font-size: 12px;
  color: var(--tx2, #94a3b8) !important;
  white-space: nowrap;
}

/* ── Badges ───────────────────────────────────────────────────────── */
.fr-badge {
  display: inline-block;
  font-size: 10.5px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
}
.badge-credit   { background: #d1fae5; color: #065f46; }
.badge-debit    { background: #fee2e2; color: #991b1b; }
.badge-pending  { background: #fef3c7; color: #92400e; }
.badge-recurring{ background: #dbeafe; color: #1e40af; }
.ml-1 { margin-left: 4px; }

/* ── Amounts ──────────────────────────────────────────────────────── */
.fr-amount { font-weight: 700; font-size: 13.5px; }
.fr-amount-credit  { color: #059669; }
.fr-amount-debit   { color: #DC2626; }
.fr-amount-pending { color: #D97706; }
.fr-dash { color: var(--tx2, #cbd5e1); }

/* ── Footer Total Row ─────────────────────────────────────────────── */
.fr-tfoot-row td {
  font-weight: 700;
  font-size: 13px;
  background: var(--surface2, #f8fafc);
  border-top: 2px solid var(--border, #e0e0e0);
  padding: 13px 14px;
  color: var(--tx, #374151);
}

/* ── Empty States ─────────────────────────────────────────────────── */
.fr-empty-row {
  text-align: center;
  padding: 40px 14px !important;
  color: var(--tx2, #94a3b8) !important;
  font-size: 13px !important;
}
.fr-empty-msg {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--tx2, #94a3b8);
  font-size: 13px;
}
.fr-center-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
}

/* ── Responsive ───────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .fr-summary-grid {
    grid-template-columns: 1fr 1fr;
  }
  .fr-page-header-right {
    width: 100%;
    justify-content: flex-start;
  }
}
@media (max-width: 760px) {
  .fr-month-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .fr-month-chips {
    width: 100%;
    padding-top: 8px;
  }
  .fr-page-header {
    flex-direction: column;
    align-items: stretch;
  }
  .fr-card-header {
    flex-direction: column;
    align-items: stretch;
  }
  .fr-card-header > div:last-child {
    width: 100%;
  }
}
@media (max-width: 600px) {
  .fr-container { padding: 14px; }
  .fr-summary-grid { grid-template-columns: 1fr; }
  .fr-stmt-header { flex-direction: column; }
  .fr-table th,
  .fr-table tbody td {
    padding: 10px 12px;
  }
  .fr-table th {
    font-size: 10.5px;
  }
  .fr-table tbody td {
    font-size: 12px;
  }
  .fr-btn {
    width: 100%;
    justify-content: center;
  }
  .fr-page-header-right {
    justify-content: space-between;
  }
}
</style>
